import { useCallback, useEffect, useState } from 'react';
import { Archive, CalendarClock, ChevronDown, ChevronRight, Download, Loader2, Save } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { useAuth } from '../../contexts/AuthContext';
import { cn } from '../../lib/utils';

const when = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '-');
const STATUS = {
    running: ['Saving...', 'bg-sky-500/10 text-sky-700'],
    completed: ['Saved', 'bg-emerald-500/10 text-emerald-700'],
    partial: ['Partly saved', 'bg-amber-500/10 text-amber-700'],
    failed: ['Failed', 'bg-red-500/10 text-red-700'],
};

const ReportFile = ({ snapshot, report, onDownload, busy }) => (
    <div className="flex items-center justify-between gap-3 rounded-lg border bg-background px-3 py-2" data-testid={`archive-file-${snapshot.id}-${report.key}`}>
        <div className="min-w-0">
            <p className="truncate text-sm font-medium">{report.label}</p>
            <p className="text-xs text-muted-foreground">{report.status === 'saved' ? `${report.rows} row${report.rows === 1 ? '' : 's'}` : 'Could not be saved'}</p>
        </div>
        {report.status === 'saved' && (
            <Button size="sm" variant="outline" className="gap-1.5" disabled={busy} onClick={() => onDownload(snapshot, report)}
                data-testid={`archive-download-${snapshot.id}-${report.key}`}>
                {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />} CSV
            </Button>
        )}
    </div>
);

const SnapshotRow = ({ snapshot, open, onToggle, onDownload, downloading }) => {
    const [label, tone] = STATUS[snapshot.status] || STATUS.failed;
    const saved = snapshot.reports.filter(r => r.status === 'saved').length;
    return (
        <div className="rounded-xl border" data-testid={`archive-snapshot-${snapshot.id}`}>
            <button type="button" onClick={onToggle} className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-muted/40"
                data-testid={`archive-toggle-${snapshot.id}`}>
                {open ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
                <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold">{when(snapshot.started_at)}</p>
                    <p className="text-xs text-muted-foreground">
                        {snapshot.trigger === 'scheduled' ? 'Daily copy' : `Saved by ${snapshot.requested_by || 'an admin'}`}
                        {snapshot.status !== 'running' && ` · ${saved} of ${snapshot.reports.length} reports`}
                    </p>
                </div>
                <Badge className={cn('border-0 hover:bg-transparent', tone)} data-testid={`archive-status-${snapshot.id}`}>{label}</Badge>
            </button>
            {open && snapshot.reports.length > 0 && (
                <div className="grid gap-2 border-t bg-muted/20 p-3 sm:grid-cols-2 lg:grid-cols-3">
                    {snapshot.reports.map(r => (
                        <ReportFile key={r.key} snapshot={snapshot} report={r} onDownload={onDownload} busy={downloading === `${snapshot.id}:${r.key}`} />
                    ))}
                </div>
            )}
        </div>
    );
};

export const ReportArchive = () => {
    const { api } = useAuth();
    const [data, setData] = useState(null);
    const [openId, setOpenId] = useState(null);
    const [saving, setSaving] = useState(false);
    const [downloading, setDownloading] = useState(null);

    const load = useCallback(async () => {
        try {
            const res = await api.get('/reports/archive');
            setData(res.data);
        } catch {
            toast.error('Could not load the report archive');
        }
    }, [api]);

    useEffect(() => { load(); }, [load]);

    const running = data?.snapshots?.some(s => s.status === 'running');
    useEffect(() => {
        if (!running) return undefined;
        const t = setInterval(load, 4000);
        return () => clearInterval(t);
    }, [running, load]);

    const saveNow = async () => {
        setSaving(true);
        try {
            const res = await api.post('/reports/archive/run');
            setOpenId(res.data.snapshot.id);
            toast.success('Saving a copy of every report...');
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Could not start saving reports');
        } finally {
            setSaving(false);
        }
    };

    const download = async (snapshot, report) => {
        setDownloading(`${snapshot.id}:${report.key}`);
        try {
            const res = await api.get(`/reports/archive/${snapshot.id}/${report.key}`, { responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `${report.key}_${snapshot.started_at.slice(0, 10)}.csv`);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch {
            toast.error('Could not download this report');
        } finally {
            setDownloading(null);
        }
    };

    if (!data) {
        return <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
    }
    return (
        <div className="space-y-4" data-testid="report-archive">
            <div className="flex flex-col gap-3 rounded-xl border border-dashed p-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-start gap-3">
                    <CalendarClock className="mt-0.5 h-5 w-5 text-primary" />
                    <div>
                        <p className="text-sm font-medium" data-testid="archive-schedule">{data.schedule}</p>
                        <p className="text-xs text-muted-foreground">A full copy of every report (all time, all departments) is kept here so you have a record of each day.{data.retention_days ? ` Copies older than ${data.retention_days} days are removed automatically.` : ''}</p>
                    </div>
                </div>
                <Button onClick={saveNow} disabled={saving || running} className="gap-2" data-testid="archive-save-now-btn">
                    {saving || running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                    {running ? 'Saving...' : 'Save a copy now'}
                </Button>
            </div>
            {data.snapshots.length === 0 ? (
                <div className="py-12 text-center" data-testid="archive-empty">
                    <Archive className="mx-auto mb-3 h-10 w-10 text-muted-foreground/50" />
                    <p className="text-muted-foreground">No saved copies yet. The first one is saved tonight, or click "Save a copy now".</p>
                </div>
            ) : (
                <div className="space-y-2">
                    {data.snapshots.map(s => (
                        <SnapshotRow key={s.id} snapshot={s} open={openId === s.id} onToggle={() => setOpenId(openId === s.id ? null : s.id)}
                            onDownload={download} downloading={downloading} />
                    ))}
                </div>
            )}
        </div>
    );
};
